/**
 * Toast notifications and a promise-based confirmation dialog. They replace the
 * original's `alert()` / `confirm()` calls while keeping their exact messages.
 */
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '@/services/api';

type Kind = 'error' | 'success' | 'info';
interface Toast {
  id: number;
  kind: Kind;
  text: string;
}
interface ConfirmState {
  message: string;
  okLabel: string;
  danger: boolean;
  resolve: (v: boolean) => void;
}

interface NotifyApi {
  error(msg: unknown): void;
  success(msg: string): void;
  info(msg: string): void;
  confirm(message: string, opts?: { okLabel?: string; danger?: boolean }): Promise<boolean>;
}

const Ctx = createContext<NotifyApi | null>(null);

export function NotifyProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirmState, setConfirm] = useState<ConfirmState | null>(null);
  const seq = useRef(0);

  const push = useCallback((kind: Kind, text: string) => {
    const id = ++seq.current;
    setToasts((t) => [...t.slice(-4), { id, kind, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === 'error' ? 8000 : 4000);
  }, []);

  const value: NotifyApi = {
    error: (m) => push('error', m instanceof ApiError || m instanceof Error ? m.message : String(m)),
    success: (m) => push('success', m),
    info: (m) => push('info', m),
    confirm: (message, opts) =>
      new Promise<boolean>((resolve) => setConfirm({ message, okLabel: opts?.okLabel || 'موافق', danger: !!opts?.danger, resolve })),
  };

  const close = (v: boolean) => {
    confirmState?.resolve(v);
    setConfirm(null);
  };

  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="toast-wrap no-print" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={'toast toast-' + t.kind}>
            {t.text}
          </div>
        ))}
      </div>
      {confirmState && (
        <div className="modal-bg" style={{ zIndex: 75 }} onClick={() => close(false)}>
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-text"
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.key === 'Escape' && close(false)}
          >
            <p id="confirm-text" className="whitespace-pre-wrap text-sm leading-7 text-[#5C1528]">
              {confirmState.message}
            </p>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                autoFocus
                className={confirmState.danger ? 'btn-p flex-1 py-2.5' : 'btn-p flex-1 py-2.5'}
                style={confirmState.danger ? { background: '#c62828' } : undefined}
                onClick={() => close(true)}
              >
                {confirmState.okLabel}
              </button>
              <button type="button" className="btn-o flex-1 py-2.5" onClick={() => close(false)}>
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}

export function useNotify(): NotifyApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useNotify outside NotifyProvider');
  return v;
}
